// Login - user authentication page
// Implements US-1.1: User Authentication with Development Mode

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getTestUsers, loginDev, loginSSO, TestUser } from '../services/authService';
import { storeToken } from '../utils/tokenStorage';
import { disconnectSocket } from '../services/socketService';
import styles from './Login.module.css';

/**
 * RENDER login page with dev mode or production mode UI
 * Implements dual-mode authentication based on AUTH_MODE environment variable
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const authMode = process.env.REACT_APP_AUTH_MODE || 'production';

  // DEV MODE state
  const [testUsers, setTestUsers] = useState<TestUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // GENERAL state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * LOAD test users on mount if in dev mode
   */
  useEffect(() => {
    if (authMode === 'dev') {
      loadTestUsers();
    }
  }, [authMode]);

  /**
   * FETCH test users from backend
   */
  const loadTestUsers = async () => {
    try {
      const users = await getTestUsers();
      setTestUsers(users);
      // SELECT first user by default
      if (users.length > 0 && users[0]) {
        setSelectedUserId(users[0].id);
      }
    } catch (err) {
      setError('Failed to load test users. Is the backend running?');
      console.error('Load test users error:', err);
    }
  };

  /**
   * HANDLE dev mode login submission
   */
  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // VALIDATE user selected
      if (!selectedUserId) {
        setError('Please select a test user');
        setLoading(false);
        return;
      }

      // AUTHENTICATE with backend
      const response = await loginDev(selectedUserId);

      if (response.success && response.token) {
        // DISCONNECT any existing socket before logging in
        // This prevents socket leaks from previous sessions
        disconnectSocket();

        // STORE token in sessionStorage
        storeToken(response.token);

        // REDIRECT to workspaces page
        navigate('/workspaces');
      } else {
        setError(response.error || 'Login failed');
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * HANDLE SSO login (placeholder for production mode)
   */
  const handleSSOLogin = async () => {
    setError('');
    setLoading(true);

    try {
      // PLACEHOLDER - Keycloak integration not yet implemented
      await loginSSO('placeholder-token');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('SSO login not yet implemented');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className={styles.content}>
        {/* TITLE */}
        <h1 className={styles.title}>Login to Arbiter</h1>
        <p className={styles.subtitle}>
          Sign in to create and manage your prioritization workspaces
        </p>

        {/* ERROR message */}
        {error && (
          <div className={styles.errorNotice}>
            <strong>Error</strong>
            {error}
          </div>
        )}

        {/* DEV MODE login form */}
        {authMode === 'dev' && (
          <>
            <div className={styles.devModeBadge}>Development Mode</div>
            <form className={styles.form} onSubmit={handleDevLogin}>
              <div className={styles.formGroup}>
                <label htmlFor="testUser" className={styles.label}>
                  Select Test User
                </label>
                <select
                  id="testUser"
                  className={styles.select}
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  disabled={loading || testUsers.length === 0}
                >
                  {testUsers.length === 0 && <option>Loading users...</option>}
                  {testUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className={styles.button}
                disabled={loading || !selectedUserId}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          </>
        )}

        {/* PRODUCTION MODE SSO login */}
        {authMode === 'production' && (
          <>
            <button
              className={styles.button}
              onClick={handleSSOLogin}
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In with Keycloak SSO'}
            </button>
            <p className={styles.ssoNote}>
              SSO integration coming soon. Use development mode for now.
            </p>
          </>
        )}

        {/* DIVIDER */}
        <div className={styles.divider}>or</div>

        {/* LINK to guest access */}
        <div className={styles.link}>
          Don't have an account? <Link to="/guest">Join as a Guest</Link>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
